import { useEffect, useState } from 'react';
import { Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PRODUCT } from '@/config/product';
import qrAppMotorista from '@/assets/qr-app-motorista.svg';

interface VersaoPublicada {
  versao: string;
  publicadoEm: string;
  tamanhoBytes: number;
}

/**
 * Bloco de download do app do motorista.
 *
 * Consulta o `versao.json` antes de oferecer o arquivo. Só mostra o botão se
 * houver algo publicado — um link para APK inexistente faria o gestor mandar
 * o motorista baixar um 404, e o suporte disso volta como "o app não funciona".
 *
 * O QR Code é um SVG gerado em tempo de build a partir da mesma URL. Não há
 * biblioteca de QR no bundle: a URL é fixa, então gerar em tempo de execução
 * custaria uns 20 KB de JavaScript para desenhar sempre a mesma imagem.
 */
export function BaixarApp() {
  const [versao, setVersao] = useState<VersaoPublicada | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let ativo = true;
    fetch(PRODUCT.appVersaoUrl, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((v: VersaoPublicada | null) => ativo && setVersao(v))
      // Falha de rede e ausência do arquivo dão no mesmo: não oferecemos o
      // download. Não há por que mostrar erro — o gestor não pode agir sobre
      // isso, e a instrução alternativa já está na tela.
      .catch(() => ativo && setVersao(null))
      .finally(() => ativo && setCarregando(false));
    return () => {
      ativo = false;
    };
  }, []);

  if (carregando) {
    return (
      <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
        Verificando a versão publicada…
      </div>
    );
  }

  if (!versao) {
    return (
      <div className="rounded-md border border-warning/40 bg-warning/5 p-3 text-xs">
        <p className="font-medium text-warning-foreground">
          O app ainda não foi publicado
        </p>
        <p className="mt-1 text-muted-foreground">
          Assim que a primeira versão sair, o link de download e o QR Code
          aparecem aqui. Enquanto isso, fale com{' '}
          <a href={`mailto:${PRODUCT.supportEmail}`} className="text-primary hover:underline">
            {PRODUCT.supportEmail}
          </a>
          .
        </p>
      </div>
    );
  }

  const megabytes = (versao.tamanhoBytes / 1024 / 1024).toFixed(0);
  const publicado = new Date(versao.publicadoEm).toLocaleDateString('pt-BR');

  return (
    <div className="rounded-md border bg-muted/30 p-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <img
          src={qrAppMotorista}
          alt={`QR Code para baixar o app ${PRODUCT.name} no celular`}
          className="size-28 shrink-0 self-center rounded border bg-white p-1.5"
        />

        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-sm font-medium">
            <Smartphone className="size-4" aria-hidden />
            App do motorista — Android
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Aponte a câmera do celular para o código, ou use o botão abaixo.
          </p>

          <Button asChild size="sm" className="mt-3">
            <a href={PRODUCT.appAndroidUrl} download>
              <Download className="size-4" aria-hidden />
              Baixar o app ({megabytes} MB)
            </a>
          </Button>

          <p className="mt-2 text-[11px] text-muted-foreground">
            Versão {versao.versao} · publicada em {publicado}
          </p>
        </div>
      </div>

      {/* Este aviso evita a dúvida mais comum de quem instala APK fora da loja.
          Sem ele, o Android mostra um alerta genérico e o motorista desiste
          achando que o arquivo é suspeito. */}
      <p className="mt-3 border-t pt-3 text-[11px] text-muted-foreground">
        Na primeira instalação o Android pede para permitir a instalação de
        fontes desconhecidas. É esperado: o app é distribuído por nós, ainda
        não pela Play Store. Toque em <strong>Configurações</strong> na própria
        mensagem e autorize o navegador.
      </p>
    </div>
  );
}
